<template>
  <div class="w-full">
    <div
      class="relative flex flex-col"
      :class="position"
    >
      <div
        v-if="canReset"
        class="absolute right-0 text-xs cursor-pointer bottom-full text-main-primary"
        @click="
          clearSelectedOptions();
          $emit('enable');
        "
      >
        Reset Filter
      </div>
      <div
        :ref="`multiSelector-${uid}`"
        :class="`flex w-full border rounded border-gray-300 bg-white px-1 shadow-sm ${selectedOptions.length ? '!py-1.5' : '!max-h-[47px] !py-2'} ${customClass} ${disabled ? 'pointer-events-none cursor-not-allowed bg-zinc-300' : 'cursor-pointer bg-white'}`"
      >
        <div
          v-if="topLabel"
          class="absolute flex items-center mb-5 space-x-1 top-1"
        >
          <p class="text-xs font-normal text-[#707C8ECC]">
            {{ topLabel }}
          </p>
        </div>
        <div class="flex items-end">
          <slot name="icon" />
        </div>
        <div class="flex flex-wrap flex-auto ">
          <template v-if="!topLabel">
            <div
              v-for="(option, index) in selectedOptions"
              :key="index"
              class="flex items-center justify-center gap-1 px-2 py-1 m-1 border rounded-full no-localize-translate"
              :class="{
                'border-emerald-300 bg-emerald-100 text-zuno-turquoise':
                  !disabled,
                'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400':
                  disabled,
              }"
            >
              <span class="flex-initial max-w-full text-xs font-normal leading-none ">{{ option.name }}</span>
              <div
                v-if="!disabled"
                class="flex flex-row-reverse flex-auto cursor-pointer"
                @click.stop="deselectOption(option.value)"
              >
                <TimesSmall custom-style="h-3.5 w-3.5" />
              </div>
            </div>
          </template>
          <button
            v-if="topLabel"
            class="mt-3.5 ml-1 text-xs font-normal text-primary"
            @click="toggleSelector"
          >
            {{ labelPlaceholder }}
          </button>
          <button
            v-if="!selectedOptions.length && !topLabel"
            class="px-4 py-1 text-sm italic font-medium"
            :class="disabled ? 'text-zinc-400' : ''"
            :disabled="disabled"
            @click="toggleSelector"
          >
            {{ placeholderText }}
          </button>
          <div class="flex-1">
            <input
              :placeholder="inputPlaceholder"
              readonly
              aria-readonly="true"
              type="text"
              class="w-full h-full p-1 px-2 bg-transparent bg-red-500 outline-none opacity-0 appearance-none no-localize-translate placeholder:text-sm placeholder:font-light placeholder:italic"
              role="combobox"
              aria-controls="options"
              aria-expanded="false"
              :disabled="disabled"
              @click="toggleSelector"
            >
          </div>
        </div>
        <button
          type="button"
          class="flex items-center gap-2 pl-2 pr-1 text-gray-600 cursor-pointer no-localize-translate focus:outline-none"
          :disabled="disabled"
          @click.stop="toggleSelector"
        >
          <span class="text-gray-300">|</span>
          <ChevronDownSmall
            class="text-gray-500 duration-150 transform"
            :class="{ 'rotate-180': comboOpen }"
          />
        </button>
      </div>
      <div
        v-show="comboOpen"
        :ref="`multiSelector-options-${uid}`"
        :class="listBoxClass"
        class="fixed z-50 py-1 mt-2 overflow-auto bg-white border-2 border-gray-200 shadow-lg h-min max-h-96 min-w-max"
        :style="{
          top: dropdownTop + 'px',
          left: dropdownLeft + 'px',
          width: dropdownWidth + 'px',
        }"
        @scroll="handleScroll($event)"
      >
        <div class="relative py-1 pl-3 pr-2 overflow-hidden border-b border-transparent border-b-gray-300">
          <div class="absolute left-4 top-3">
            <SearchIcon class="w-5 h-5 mx-auto text-slate-600" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full p-2 pl-8 text-sm border-transparent border-none focus:shadow-outline ring-0 focus:border-transparent focus:ring-0"
            placeholder="Search..."
          >
          <button
            v-if="searchQuery"
            type="button"
            class="no-localize-translate absolute right-4 top-1.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-green-light/80 text-main-primary hover:text-gray-700 focus:outline-none"
            @click.stop="clearInput"
          >
            <TimesSmall custom-style="w-4 h-4" />
          </button>
        </div>
        <ul
          class="text-base ring-1 ring-black/5 focus:outline-none sm:text-sm"
          role="listbox"
        >
          <li
            v-for="{ active, id, name, selected } in filteredOptions"
            :key="`${uid}-${id}`"
            class="relative py-2 pl-3 text-gray-900 cursor-default select-none pr-9"
            :class="{
              'bg-zuno-turquoise text-slate-50': active,
              'text-gray-900': !active,
            }"
            role="option"
            tabindex="-1"
            @click.stop="toggleSelection(id)"
            @mouseenter="toggleActivation(id, 'on')"
            @mouseleave="toggleActivation(id)"
          >
            <span
              class="block truncate no-localize-translate"
              :class="{ 'font-semibold': selected }"
            >
              {{ name }}</span>
            <span
              v-if="selected"
              class="absolute inset-y-0 right-0 flex items-center pr-4"
              :class="{
                'text-slate-50': active,
                'text-zuno-turquoise': !active,
              }"
            >
              <CheckSmall />
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'MultiSelector',

  props: {
    optionsToEdit: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      required: true,
    },
    displayProp: {
      type: String,
      required: true,
    },
    valueProp: {
      type: String,
      required: true,
    },
    placeholderText: {
      type: String,
      default: 'Please select...',
    },
    listBoxClass: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: 'items-center',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    nextPage: {
      type: String,
      default: null,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalDataCount: {
      type: Number,
      default: 0,
    },
    topLabel: {
      type: String,
      default: '',
    },
    labelPlaceholder: {
      type: String,
      default: '',
    },
    resetAsFilter: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['fetchNextPage', 'valuesUpdated', 'input', 'enable'],

  data() {
    return {
      uid: null,
      dropdownTop: 0,
      dropdownLeft: 0,
      dropdownWidth: 0,
      definedOptions: this.attachDefaultOptionsProps([...this.options]),
      comboOpen: false,
      selectedOptions: [],
      searchQuery: '',
      isFetchingNextPage: false,
      threshold: 50,
    }
  },
  computed: {
    inputPlaceholder() {
      return this.selectedOptions.length === 0 ? 'Please select...' : ''
    },
    filteredOptions() {
      return !this.searchQuery ? this.definedOptions : [...this.definedOptions].filter(({ name }) => name.toLowerCase().includes(this.searchQuery.toLowerCase()))
    },
    canReset() {
      return !this.comboOpen && this.resetAsFilter && !!this.selectedOptions.length
    },
  },

  watch: {
    comboOpen(val) {
      nextTick(() => {
        if (val) {
          document.body.addEventListener('click', this.handleOutsideClick)
          document.body.addEventListener('keydown', this.handleEscapeKey)
          this.positionDropdown()
          this.positionDropdown = debounce(this.positionDropdown, 50)
          document.addEventListener('scroll', this.positionDropdown, true)
        }
        else {
          document.body.removeEventListener('click', this.handleOutsideClick)
        }
      })
    },
    options(newOptions) {
      this.definedOptions = this.attachDefaultOptionsProps(newOptions).map(
        (option) => {
          option.selected = [...this.optionsToEdit]
            .map(newOption => newOption[this.valueProp])
            .includes(option[this.valueProp])
          return option
        },
      )
    },
    optionsToEdit: {
      handler(newOptions) {
        this.selectedOptions = this.attachDefaultOptionsProps(newOptions)
        this.definedOptions = this.definedOptions.map((option) => {
          option.selected = [...newOptions]
            .map(newOption => newOption[this.valueProp])
            .includes(option[this.valueProp])
          return option
        })
      },
      immediate: true,
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  methods: {
    handleScroll(event) {
      clearTimeout(this.debounceTimeout)

      this.debounceTimeout = setTimeout(() => {
        this.processScroll(event)
      }, 200)
    },
    async processScroll(event) {
      const target = event?.target

      if (!target) {
        return
      }

      const scrollTop = target.scrollTop
      const scrollHeight = target.scrollHeight
      const offsetHeight = target.offsetHeight

      if (this.isFetchingNextPage || !this.nextPage) {
        return
      }

      if (scrollHeight - scrollTop - offsetHeight < this.threshold) {
        try {
          this.isFetchingNextPage = true
          await this.$emit('fetchNextPage')
        }
        catch (error) {
          return error
        }
        finally {
          this.isFetchingNextPage = false
        }
      }
    },

    positionDropdown() {
      const multiSelector = this.$refs[`multiSelector-${this.uid}`]
      const multiSelectorOptions = this.$refs[`multiSelector-options-${this.uid}`]
      const { top, right, left } = multiSelector.getBoundingClientRect()
      const {
        height: multiSelectorOptionsHeight,
        width: multiSelectorOptionsWidth,
      } = multiSelectorOptions.getBoundingClientRect()
      const { height: multiSelectorHeight, width: multiSelectWidth }
        = multiSelector.getBoundingClientRect()
      const { innerHeight, innerWidth } = window

      if (
        top + multiSelectorHeight + multiSelectorOptionsHeight
        > innerHeight
      ) {
        this.dropdownTop = top - multiSelectorOptionsHeight
      }
      else {
        this.dropdownTop = top + multiSelectorHeight
      }

      if (left + multiSelectorOptionsWidth > innerWidth) {
        this.dropdownLeft = innerWidth - multiSelectorOptionsWidth - 10
      }
      else {
        this.dropdownLeft = right - multiSelectWidth
      }

      this.dropdownWidth = multiSelectWidth
    },
    handleOutsideClick(event) {
      // If the clicked target is not within the dropdown list or the dropdown input,
      // set comboOpen to false to close the dropdown and remove the click event listener from the document.
      if (
        !this.$refs[`multiSelector-options-${this.uid}`].contains(
          event.target,
        )
        && !this.$refs[`multiSelector-${this.uid}`].contains(event.target)
      ) {
        this.closeDropdownAndkillAllListeners()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeDropdownAndkillAllListeners()
      }
    },
    killAllListeners() {
      document.body.removeEventListener('click', this.handleOutsideClick)
      document.body.removeEventListener('keydown', this.handleEscapeKey)
      document.removeEventListener('scroll', this.positionDropdown, true)
      this.searchQuery = ''
    },
    closeDropdownAndkillAllListeners() {
      this.closeCombo()
      this.killAllListeners()
    },
    closeCombo() {
      this.comboOpen = false
    },
    attachDefaultOptionsProps(options) {
      const optionsToAttach = JSON.parse(JSON.stringify(options))

      return optionsToAttach.map((option) => {
        option.active = false
        option.selected = false
        option.name = option[this.displayProp]
        option.value = option[this.valueProp]
        return option
      })
    },
    toggleActivation(id, status = 'off') {
      this.definedOptions = this.definedOptions.map((option) => {
        if (id === option.id) {
          option.active = status === 'on'
        }
        return option
      })
    },
    clearInput() {
      this.searchQuery = ''
    },
    toggleSelection(id) {
      this.definedOptions = [...this.definedOptions].map((option) => {
        if (id === option.id) {
          option.selected = !option.selected
        }
        return option
      })
      this.selectedOptions = [...this.definedOptions].filter(
        ({ selected }) => selected,
      )
      this.$emit(
        'valuesUpdated',
        [...this.selectedOptions].map(option => option.value),
      )
      this.$emit(
        'input',
        [...this.selectedOptions].map(option => option.value),
      )
    },
    deselectOption(value) {
      const optionId = this.definedOptions.find(option => option.value === value).id
      this.toggleSelection(optionId)
    },
    toggleSelector() {
      this.comboOpen = !this.comboOpen
      nextTick(() => {
        if (!this.isOpen) {
          this.killAllListeners()
        }
      })
    },
    clearSelectedOptions() {
      this.definedOptions = [...this.definedOptions].map((option) => {
        option.selected = false
        return option
      })
      this.selectedOptions = []
      this.$emit(
        'valuesUpdated',
        [...this.selectedOptions].map(option => option.value),
      )
    },
  },
}
</script>
